import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionTitle } from "@/components/common";
import { toast } from "sonner";
import { Building2, User, FileWarning, Send, ChevronRight, Clock } from "lucide-react";

export default function DocumentazioneInCorso() {
  const nav = useNavigate();
  const { isSegreteria } = useAuth();
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/dashboard/documentazione-in-corso").then((r) => setItems(r.data)).catch((e) => { toast.error(apiError(e)); setItems([]); });
  useEffect(() => { load(); }, []);

  const sollecito = async (e, im) => {
    e.stopPropagation();
    if (!window.confirm(`Inviare un sollecito all'agente ${im.agente || ""} per i documenti mancanti di "${im.titolo || im.indirizzo}"?`)) return;
    setBusy(im.id);
    try { await api.post(`/immobili/${im.id}/sollecito-agente`); toast.success("Sollecito inviato all'agente. Immobile in attesa dei documenti."); load(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(null); }
  };

  if (!items) return <div className="skeleton h-96 rounded-2xl" />;

  return (
    <div className="fade-up" data-testid="doc-in-corso-page">
      <SectionTitle sub="Immobili con documentazione incompleta. Tocca una scheda per vedere i documenti mancanti.">Documentazione in corso</SectionTitle>
      {items.length === 0 ? (
        <div className="card-immo p-12 text-center text-sm text-[var(--muted)] mt-5" data-testid="doc-in-corso-empty">Nessun immobile con documenti mancanti.</div>
      ) : (
        <div className="space-y-3 mt-5" data-testid="doc-in-corso-list">
          {items.map((im) => {
            const mancanti = im.doc_mancanti ?? ((im.doc_totali || 0) - (im.doc_verificati || 0));
            return (
              <div key={im.id} data-testid={`doc-row-${im.id}`} onClick={() => nav(`/immobili/${im.id}?tab=docs`)}
                   className={`card-immo p-4 flex items-center gap-3 cursor-pointer hover:-translate-y-0.5 transition-transform ${im.attesa ? "border-[#FFB020]/50 bg-[#FFB020]/5" : ""}`}>
                <div className="w-9 h-9 rounded-lg bg-[#FFB020]/10 text-[#FFB020] flex items-center justify-center shrink-0"><FileWarning size={17} /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate flex items-center gap-2"><Building2 size={13} className="text-[#FC82ED]" /> {im.titolo || im.indirizzo || "Immobile"}</div>
                  <div className="text-[11px] text-[var(--muted)] flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                    <span className="flex items-center gap-1"><User size={11} />{im.agente || "—"}</span>
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full border text-red-600 bg-red-100 border-red-300 text-[10px] font-bold">{mancanti} mancant{mancanti === 1 ? "e" : "i"} · {im.doc_verificati || 0}/{im.doc_totali || 0}</span>
                    {im.attesa
                      ? <span data-testid={`attesa-badge-${im.id}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[#FFB020] bg-[#FFB020]/10 border-[#FFB020]/40 text-[10px] font-bold uppercase"><Clock size={10} /> In attesa{im.attesa_at ? ` · ${fmtDate(im.attesa_at)}` : ""}</span>
                      : <span className="uppercase tracking-wide">{(im.checklist_stato || im.stato || "").replace(/_/g, " ")}</span>}
                  </div>
                </div>
                {isSegreteria && (im.attesa ? (
                  <button data-testid={`risollecito-${im.id}`} disabled={busy === im.id} onClick={(e) => sollecito(e, im)}
                          className="shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-[#FFB020]/40 text-[#FFB020] text-xs font-semibold hover:bg-[#FFB020]/10 disabled:opacity-60">
                    <Send size={13} /> Risollecita
                  </button>
                ) : (
                  <button data-testid={`sollecito-${im.id}`} disabled={busy === im.id} onClick={(e) => sollecito(e, im)}
                          className="shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#FC82ED] hover:bg-[#e06dd2] text-black text-xs font-semibold glow-pink disabled:opacity-60">
                    <Send size={13} /> Invia sollecito
                  </button>
                ))}
                <ChevronRight size={16} className="text-[var(--muted)] shrink-0" />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
